import React, { useState, useEffect } from 'react';
import { Platform, View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import FontFamily from '../../../../constants/fonts';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import { AdminColors } from '../../constants/adminColors';
import EquipmentConfig, { ListLanguageToggle } from './EquipmentConfig';
import { t } from './i18n';
import { useTheme } from '../../../../theme/useTheme';
import PhotoConfig from './PhotoConfig';
import RouteTypeConfig from './RouteTypeConfig';
import LoadSequenceConfig from './LoadSequenceConfig';
import { PerfectTruckScreenProps, EquipmentLanguage } from './types';
import { fetchLastUpdated } from './perfectTruckApi';
import { formatUserName, formatDate, formatTimeAmPm } from '../../../PerfectTruck/perfectTruckUtils';

const SUPER_USER_ROLES = ['Super User', 'Developer'];

type ConfigView = 'equipment-config' | 'photo-config' | 'route-config' | 'load-sequence-config';

// --- Access Denied ------------------------------------------------------------
const AccessDenied: React.FC = () => (
  <View style={styles.accessDeniedContainer}>
    <MaterialIcons name="lock" size={48} color={AdminColors.gray150} />
    <Text style={styles.accessDeniedTitle}>Access Denied</Text>
    <Text style={styles.accessDeniedMessage}>
      You do not have permission to access the Perfect Truck configuration.
      Only Super Users can manage these settings.
    </Text>
  </View>
);

// --- Screen -------------------------------------------------------------------
const PerfectTruckScreen: React.FC<PerfectTruckScreenProps> = ({ onHomePress }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const [view, setView] = useState<ConfigView>('equipment-config');
  const [listLanguage, setListLanguage] = useState<EquipmentLanguage>('en');
  const [lastUpdatedText, setLastUpdatedText] = useState<string | null>(null);
  const [lastUpdatedRaw, setLastUpdatedRaw] = useState<any | null>(null);
  const userRole = useSelector((state: RootState) => state.appData?.userInfo?.roleName ?? '');
  const isSuperUser = SUPER_USER_ROLES.includes(userRole);

  useEffect(() => {
    fetchLastUpdated()
      .then(res => {
        setLastUpdatedRaw(res || null);
      })
      .catch(() => { setLastUpdatedRaw(null); });
  }, []);

  useEffect(() => {
    if (!lastUpdatedRaw) { setLastUpdatedText(null); return; }
    const res = lastUpdatedRaw;
    if (res.updatedBy || res.updatedAt) {
      const name = res.updatedBy ? formatUserName(res.updatedBy) : '';
      const date = res.updatedAt ? formatDate(res.updatedAt) : '';
      const time = res.updatedAt ? formatTimeAmPm(res.updatedAt) : '';
      const parts = [t(listLanguage, 'lastUpdated.last')];
      if (name) parts.push(`${t(listLanguage, 'lastUpdated.by')} ${name}`);
      if (date) parts.push(`${t(listLanguage, 'lastUpdated.on')} ${date}`);
      if (time) parts.push(`${t(listLanguage, 'lastUpdated.at')} ${time}`);
      setLastUpdatedText(parts.join(' '));
    } else if (res.displayText) {
      setLastUpdatedText(res.displayText);
    } else {
      setLastUpdatedText(null);
    }
  }, [lastUpdatedRaw, listLanguage]);

  const tabs: { key: ConfigView; label: string }[] = [
    { key: 'equipment-config',      label: t(listLanguage, 'tabs.equipment') },
    { key: 'photo-config',          label: t(listLanguage, 'tabs.photos') },
    { key: 'route-config',          label: t(listLanguage, 'tabs.routes') },
    { key: 'load-sequence-config',  label: t(listLanguage, 'tabs.loadSequences') },
  ];

  return (
    <View style={[styles.wrapper, isDark && { backgroundColor: colors.background.page }]}>
      {/* Header: breadcrumb + title */}
      <View style={[styles.header, isDark && { backgroundColor: colors.background.page }]}>
        <View style={styles.headerTop}>
          <View style={styles.headerLeft}>
            <AdminBreadcrumb
              items={[
                { label: t(listLanguage, 'breadcrumb.configurations'), onPress: onHomePress },
                { label: t(listLanguage, 'breadcrumb.perfectTruck') },
              ]}
            />
            <Text style={[styles.pageTitle, isDark && { color: colors.text.primary }]}>{t(listLanguage, 'pageTitle')}</Text>
            {lastUpdatedText ? (
              <Text style={[styles.pageSubtitle, isDark && { color: colors.text.secondary }]}>{lastUpdatedText}</Text>
            ) : null}
          </View>
          <ListLanguageToggle value={listLanguage} onChange={setListLanguage} />
        </View>

        {/* Tab bar */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabBar, isDark && { borderBottomColor: colors.border.divider }]}
          contentContainerStyle={styles.tabBarContent}
        >
          {tabs.map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                view === tab.key && styles.tabActive,
                isDark && view === tab.key && { borderBottomColor: colors.brand.primary },
              ]}
              onPress={() => setView(tab.key)}
              activeOpacity={0.7}
            >
              <Text style={[
                styles.tabLabel,
                view === tab.key && styles.tabLabelActive,
                isDark && { color: colors.text.secondary },
                isDark && view === tab.key && { color: colors.brand.primary },
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Config sub-screens */}
      {view === 'equipment-config' && (
        isSuperUser
          ? <ScrollView style={styles.tabContent}><EquipmentConfig listLanguage={listLanguage} onListLanguageChange={setListLanguage} /></ScrollView>
          : <AccessDenied />
      )}
      {view === 'photo-config' && (
        isSuperUser
          ? <ScrollView style={styles.tabContent}><PhotoConfig listLanguage={listLanguage} /></ScrollView>
          : <AccessDenied />
      )}
      {view === 'route-config' && (
        isSuperUser
          ? <ScrollView style={styles.tabContent}><RouteTypeConfig listLanguage={listLanguage} /></ScrollView>
          : <AccessDenied />
      )}
      {view === 'load-sequence-config' && (
        isSuperUser
          ? <ScrollView style={styles.tabContent}><LoadSequenceConfig listLanguage={listLanguage} /></ScrollView>
          : <AccessDenied />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: { flex: 1, backgroundColor: AdminColors.bgPage },
  header: {
    paddingHorizontal: 32,
    paddingTop: 32,
    backgroundColor: AdminColors.bgPage,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  headerLeft: { flex: 1 },
  pageTitle: {
    fontSize: 28,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    lineHeight: 40,
    marginTop: 4,
  },
  pageSubtitle: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textMuted,
    marginTop: 4,
  },
  tabBar: {
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
  },
  tabBarContent: {
    flexDirection: 'row',
    paddingRight: 8,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
    marginBottom: -2,
  },
  tabActive: {
    borderBottomColor: AdminColors.blue700,
  },
  tabLabel: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: AdminColors.textSecondary,
    lineHeight: 18,
  },
  tabLabelActive: {
    fontFamily: FontFamily.bold,
    color: AdminColors.blue700,
  },
  tabContent: {
    paddingHorizontal: 32,
    paddingTop: 0,
    paddingBottom: 60,
  },
  accessDeniedContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 16,
  },
  accessDeniedTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 24,
    color: AdminColors.textPrimary,
  },
  accessDeniedMessage: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textMuted,
    textAlign: 'center',
    lineHeight: 22,
    maxWidth: 420,
  },
});

export default PerfectTruckScreen;
